/**
 * TV Display Auto-Redirect Component
 *
 * Automatically redirects users with the 'tvdisplay' role to the TV Display page
 * in fullscreen kiosk mode. This is used for TV Display computers and service accounts
 * that should only show the TV Dashboard.
 *
 * The component:
 * 1. Checks if the user has ONLY the tvdisplay role (not admin, scheduler, etc.)
 * 2. If so, redirects to /tv-display
 * 3. Attempts to enter fullscreen mode automatically
 */

import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useAuth } from '../../auth/AuthProvider.jsx';
import Logger from '../../utils/logger.js';

const TVDisplayRedirect = ({ children }) => {
  const { isAuthenticated, isLoading, roles, hasRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasRedirected = useRef(false);

  useEffect(() => {
    // Don't do anything while loading or if not authenticated
    if (isLoading || !isAuthenticated) {
      return;
    }

    // Don't redirect if already on TV display page
    if (location.pathname === '/tv-display') {
      return;
    }

    // Don't redirect more than once per session
    if (hasRedirected.current) {
      return;
    }

    // Check if user has ONLY tvdisplay role (plus viewer and authenticated which are automatic)
    // Users with admin, scheduler, maintenance, or safety should NOT be auto-redirected
    const isTVDisplayOnly =
      hasRole('tvdisplay') &&
      !hasRole('admin') &&
      !hasRole('scheduler') &&
      !hasRole('maintenance') &&
      !hasRole('safety');

    if (isTVDisplayOnly) {
      Logger.info('[TVDisplayRedirect] User has tvdisplay role only, redirecting to TV Display');
      hasRedirected.current = true;

      // Navigate to TV display with fullscreen flag
      navigate('/tv-display?kiosk=true', { replace: true });

      // Attempt to enter fullscreen mode after a short delay
      // This needs user interaction in most browsers, but Chrome kiosk mode allows it
      setTimeout(() => {
        try {
          if (document.documentElement.requestFullscreen) {
            document.documentElement.requestFullscreen().catch(err => {
              Logger.debug('[TVDisplayRedirect] Fullscreen request failed (expected in some browsers):', err.message);
            });
          }
        } catch (err) {
          Logger.debug('[TVDisplayRedirect] Fullscreen not available:', err.message);
        }
      }, 500);
    }
  }, [isAuthenticated, isLoading, roles, hasRole, navigate, location.pathname]);

  return children;
};

export default TVDisplayRedirect;
